import { readFileSync } from 'node:fs'
import { api, formatError } from '../api-client'
import { analyticsEvent, flushAndExit } from '../analytics'
import { parseArgs } from './context/args'
import { emitJSON } from './context/render'
import { levenshtein } from './context/flags'
import { resolveWorkspaceSlug } from './context'

// `driftless collection` — the object-native operational substrate. A Collection
// is a configured table (record_schema + views + lifecycle + criterion_rels); a
// Record is one typed row. This is a SEPARATE primitive from `project` (the agent
// execution loop) — they coexist. See object-native-collection-primitive.

const KNOWN_FLAGS: ReadonlySet<string> = new Set([
  'archetype', 'schema', 'views', 'criterion', 'name', 'status', 'fields', 'owner', 'json', 'help',
  'drift', 'no-drift', 'entity', 'kind', 'dedup', 'attrs', 'distill',
  // F10.2 — server-side keyset pagination for `collection records`.
  'limit', 'cursor',
  'dry-run', 'expected-version',
  // intent retrieve (collection retrieve): free-text + structured filters.
  'query', 'updated-after', 'view', 'include-archived',
])

// F10.2 — the records list endpoint is back-compat dual-shaped (F10.1): a bare
// array when no new param is passed, the `{ records, nextCursor }` keyset envelope
// when a pagination/view param is. Normalize both so the CLI renders one way and
// can surface a nextCursor regardless of which branch the server took.
export function unwrapRecordsPage(res: unknown): { records: any[]; nextCursor: string | null } {
  if (Array.isArray(res)) return { records: res, nextCursor: null }
  const obj = (res ?? {}) as { records?: unknown; nextCursor?: unknown }
  return {
    records: Array.isArray(obj.records) ? obj.records : [],
    nextCursor: typeof obj.nextCursor === 'string' ? obj.nextCursor : null,
  }
}

function assertKnownFlags(flags: Record<string, string | boolean>): void {
  const unknown = Object.keys(flags).filter((f) => !KNOWN_FLAGS.has(f))
  if (unknown.length === 0) return
  const lines = unknown.map((f) => {
    let best: string | null = null, bestDist = Infinity
    for (const known of KNOWN_FLAGS) { const d = levenshtein(f, known); if (d < bestDist) { bestDist = d; best = known } }
    const hint = best !== null && bestDist <= Math.max(2, Math.floor(f.length / 2)) ? best : null
    return `  --${f}${hint ? `  (did you mean --${hint}?)` : ''}`
  })
  console.error(`Error: unknown flag${unknown.length > 1 ? 's' : ''} for 'driftless collection':\n${lines.join('\n')}`)
  process.exit(1)
}

export const COLLECTION_HELP = `driftless collection <subcommand> [args] [flags]

Collections — the object-native operational substrate (CRM, bug-hunt, content…).
A Collection is a configured table; a Record is one typed row. Separate from
\`project\` (the agent loop) — they coexist.

To inspect one:
  1. collection list                       See the configured tables
  2. collection get <id>                   Its schema, views and lifecycle
  3. collection records <id>               Read its rows
  4. collection context <id>               The Knowledge it reads before acting (criterion topics)

Collections:
  collection add <name> --archetype <pipeline|analysis|content> [--schema @file] [--views @file] [--criterion a,b] [--owner <email|member-id|user-id>]
  collection list [--status active|archived|all] [--owner me] [--limit n] [--cursor c]
  collection search <intent> [--owner me] [--include-archived] [--updated-after <iso>] [--limit n] [--cursor c]
  collection get <id> [--view brief|full]   # brief by default; full opts into config
  collection context <id>           # the Knowledge this collection reads before acting (criterion topics, resolved)
  collection update <id> [--name "..."] [--schema @file] [--views @file] [--criterion a,b]
  collection archive <id> | collection restore <id>
  collection purge <id> --dry-run | --expected-version <n>
  collection doctor                 # health audit: off-lifecycle status, schema violations, drift, orphans

Records:
  collection record add <collection-id> [--fields '<json>'|@file] [--status <stage>]
  collection records <collection-id> [--status <stage>] [--limit <n>] [--cursor <token>]   # bounded server-side; follow nextCursor to page
  collection retrieve <collection-id> [--query "..."] [--status <stage>] [--fields '<json>'] [--entity <id>] [--drift|--no-drift] [--updated-after <iso>] [--view <name>] [--limit <n>] [--cursor <token>]   # intent search at scale: relevant records + the criterion to read first
  collection record get <collection-id> <record-id>
  collection record update <collection-id> <record-id> [--fields '<json>'|@file] [--status <stage>] [--drift|--no-drift] [--entity <id>]
  collection record history <collection-id> <record-id> [--json]   # the record's timeline: events + broker invokes, chronological
  collection record rm <collection-id> <record-id> [--dry-run]

Entities (cross-collection identity / dedup):
  collection entity add --kind <k> --name <n> [--dedup <key>] [--attrs '<json>']   # upsert (idempotent on kind+dedup)
  collection entity list [--kind <k>]

Flags: --json (machine output) · --fields/--attrs accept inline JSON or @file · --schema/--views accept @file
       --drift marks a record's anchored source as changed · --entity links a record to an Entity
`

// F1.5 — one human-readable line per timeline event. Pure (unit-testable): the
// record_event leg renders its type + a short detail (stage arrow / touched
// fields); the invoke leg renders provider.operation + status. Never the payload.
export function formatHistoryEvent(ev: any): string {
  const at = typeof ev?.at === 'string' ? ev.at : '—'
  const actor = ev?.actor ? `${ev.actor}${ev.actor_kind ? ` (${ev.actor_kind})` : ''}` : '—'
  if (ev?.kind === 'invoke') {
    const op = `${ev.provider ?? '?'}.${ev.operation ?? '?'}`
    const err = ev.error_code ? ` [${ev.error_code}]` : ''
    return `${at}  ${'invoke'.padEnd(12)}  ${actor}  ${op} — ${ev.status ?? '?'}${err}`
  }
  const d = ev?.detail ?? {}
  let extra = ''
  if (d.from !== undefined || d.to !== undefined) extra = `  ${d.from ?? '—'} → ${d.to ?? '—'}`
  else if (Array.isArray(d.fields) && d.fields.length) extra = `  fields: ${d.fields.join(', ')}`
  else if (d.status !== undefined && d.status !== null) extra = `  status: ${d.status}`
  return `${at}  ${String(ev?.type ?? 'event').padEnd(12)}  ${actor}${extra}`
}

// Read a flag that may be inline JSON or an @file reference, parsed to an object/array.
function readJsonFlag(raw: string | boolean | undefined): unknown {
  if (typeof raw !== 'string') return undefined
  let text = raw
  if (raw.startsWith('@')) {
    try { text = readFileSync(raw.slice(1), 'utf-8') } catch { console.error(`File not found: ${raw.slice(1)}`); process.exit(1) }
  }
  try { return JSON.parse(text) } catch { console.error(`Invalid JSON for flag value: ${raw.slice(0, 40)}…`); process.exit(1) }
}

export async function collectionCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const sub = positional[0]
  const isJSON = !!flags['json']

  if (flags['help'] === true || sub === 'help' || sub === undefined) {
    process.stdout.write(COLLECTION_HELP)
    process.exit(sub === undefined ? 1 : 0)
  }
  assertKnownFlags(flags)
  const ws = await resolveWorkspaceSlug()
  const str = (k: string) => (typeof flags[k] === 'string' ? (flags[k] as string) : undefined)

  try {
    // ── retrieve: intent search at 50k-record scale — relevant records PLUS the
    // criterion Knowledge to read first, in one bounded call (mirrors MCP
    // driftless_collection action:'retrieve'). Never dumps the whole table.
    if (sub === 'retrieve') {
      const cid = positional[1]
      if (!cid) { console.error('Error: `collection retrieve <collection-id> [--query ...]`.'); process.exit(1) }
      const params = new URLSearchParams()
      for (const k of ['query', 'status', 'view', 'cursor'] as const) { if (str(k)) params.set(k, str(k)!) }
      if (str('entity')) params.set('entity_id', str('entity')!)
      if (str('updated-after')) params.set('updated_after', str('updated-after')!)
      if (str('limit')) params.set('limit', str('limit')!)
      if (flags['drift'] === true) params.set('drifted', 'true')
      if (flags['no-drift'] === true) params.set('drifted', 'false')
      const fields = readJsonFlag(flags['fields']); if (fields !== undefined) params.set('fields', JSON.stringify(fields))
      const q = params.toString() ? `?${params.toString()}` : ''
      const res = await api.get(`/workspaces/${ws}/collections/${cid}/retrieve${q}`) as any
      if (isJSON) { emitJSON(res); await flushAndExit(0) }
      const records = Array.isArray(res?.records) ? res.records : []
      const criterion = Array.isArray(res?.criterion) ? res.criterion : []
      const missing = Array.isArray(res?.criterion_missing) ? res.criterion_missing : []
      if (criterion.length) console.log(`criterion (read first): ${criterion.map((c: any) => c.slug ?? c).join(', ')}`)
      if (missing.length) console.log(`⚠ missing criterion: ${missing.join(', ')}`)
      if (!records.length) console.log('No records match — broaden --query or the filters.')
      for (const r of records) console.log(`${r.status ?? '—'}  ${JSON.stringify(r.fields)}  (${r.id})`)
      if (res?.nextCursor) console.log(`\n  more — next page: collection retrieve ${cid} --cursor ${res.nextCursor}`)
      await flushAndExit(0)
    }

    // ── records sub-surface: `collection record …` / `collection records …` ──
    if (sub === 'records' || sub === 'record') {
      if (sub === 'records') {
        const cid = positional[1]
        if (!cid) { console.error('Error: `collection records <collection-id>`.'); process.exit(1) }
        // F10.2 — read bounded server-side: --limit/--cursor go to the keyset
        // endpoint (any of them opts into the { records, nextCursor } envelope per
        // F10.1). --status alone keeps the legacy bare-array shape; unwrap handles
        // both. The page is server-bounded — no client-side slice of an unbounded
        // fetch.
        const params = new URLSearchParams()
        if (str('status')) params.set('status', str('status')!)
        if (str('limit')) params.set('limit', str('limit')!)
        if (str('cursor')) params.set('cursor', str('cursor')!)
        const q = params.toString() ? `?${params.toString()}` : ''
        const res = await api.get(`/workspaces/${ws}/collections/${cid}/records${q}`)
        if (isJSON) { emitJSON(res); await flushAndExit(0) }
        const { records, nextCursor } = unwrapRecordsPage(res)
        for (const r of records) console.log(`${r.status ?? '—'}  ${JSON.stringify(r.fields)}  (${r.id})`)
        if (nextCursor) console.log(`\n  more rows — next page: collection records ${cid} --cursor ${nextCursor}`)
        await flushAndExit(0)
      }
      const action = positional[1]
      const cid = positional[2]
      if (!action || !cid) { console.error('Error: `collection record <add|get|update|rm> <collection-id> …`.'); process.exit(1) }
      if (action === 'add') {
        const body: Record<string, unknown> = {}
        const fields = readJsonFlag(flags['fields']); if (fields !== undefined) body['fields'] = fields
        if (str('status')) body['status'] = str('status')
        if (str('entity') !== undefined) body['entity_id'] = str('entity')
        const created = await api.post(`/workspaces/${ws}/collections/${cid}/records`, body)
        analyticsEvent('collection_record_add', ws, {})
        if (isJSON) emitJSON(created); else console.log(`Record created in ${cid}.`)
        await flushAndExit(0)
      }
      const rid = positional[3]
      if (!rid) { console.error(`Error: \`collection record ${action} <collection-id> <record-id>\`.`); process.exit(1) }
      if (action === 'get') {
        const r = await api.get(`/workspaces/${ws}/collections/${cid}/records/${rid}`)
        emitJSON(r); await flushAndExit(0)
      }
      // F1.5 — the record's timeline: record_events + broker invokes, one
      // chronological (ASC) chain from the API; one line per event here.
      if (action === 'history') {
        const res = await api.get(`/workspaces/${ws}/collections/${cid}/records/${rid}/history`) as any
        if (isJSON) { emitJSON(res); await flushAndExit(0) }
        const events = Array.isArray(res?.events) ? res.events : []
        if (!events.length) console.log(`No history for record ${rid} yet.`)
        for (const ev of events) console.log(formatHistoryEvent(ev))
        await flushAndExit(0)
      }
      if (action === 'update') {
        const body: Record<string, unknown> = {}
        const fields = readJsonFlag(flags['fields']); if (fields !== undefined) body['fields'] = fields
        if (str('status')) body['status'] = str('status')
        if (flags['drift'] === true) body['drifted'] = true
        if (flags['no-drift'] === true) body['drifted'] = false
        if (str('entity') !== undefined) body['entity_id'] = str('entity')
        const r = await api.patch(`/workspaces/${ws}/collections/${cid}/records/${rid}`, body)
        if (isJSON) emitJSON(r); else console.log(`Record ${rid} updated.`)
        await flushAndExit(0)
      }
      if (action === 'rm') {
        const suffix = flags['dry-run'] === true ? '?dry_run=true' : ''
        const result = await api.delete(`/workspaces/${ws}/collections/${cid}/records/${rid}${suffix}`) as any
        if (isJSON) emitJSON(result)
        else console.log(result.dry_run ? `Record delete impact: ${JSON.stringify(result.impact)}` : `Record delete: ${result.delete_outcome?.status}`)
        await flushAndExit(0)
      }
      console.error(`Unknown record action: '${action}' (add | get | update | rm).`); process.exit(1)
    }

    // ── entity sub-surface: cross-collection identity / dedup ────────────────
    if (sub === 'entity' || sub === 'entities') {
      const action = sub === 'entities' ? 'list' : positional[1]
      if (action === 'list') {
        const q = str('kind') ? `?kind=${encodeURIComponent(str('kind')!)}` : ''
        const rows = await api.get(`/workspaces/${ws}/entities${q}`)
        if (isJSON) emitJSON(rows)
        else for (const e of (rows as any[])) console.log(`${e.kind}  ${e.name}${e.dedup_key ? `  <${e.dedup_key}>` : ''}  (${e.id})`)
        await flushAndExit(0)
      }
      if (action === 'add') {
        const kind = str('kind'); const name = str('name')
        if (!kind || !name) { console.error('Error: `collection entity add --kind <k> --name <n> [--dedup <key>]`.'); process.exit(1) }
        const body: Record<string, unknown> = { kind, name }
        if (str('dedup')) body['dedup_key'] = str('dedup')
        const attrs = readJsonFlag(flags['attrs']); if (attrs !== undefined) body['attributes'] = attrs
        const e = await api.post(`/workspaces/${ws}/entities`, body) // upsert (idempotent on kind+dedup)
        analyticsEvent('collection_entity_upsert', ws, { kind })
        if (isJSON) emitJSON(e); else console.log(`Entity '${name}' (${kind}) upserted.`)
        await flushAndExit(0)
      }
      console.error(`Unknown entity action: '${action}' (add | list).`); process.exit(1)
    }

    // ── doctor: health audit of the substrate ────────────────────────────────
    if (sub === 'doctor') {
      const a = await api.get(`/workspaces/${ws}/collections/doctor`) as any
      if (isJSON) { emitJSON(a); await flushAndExit(0) }
      const c = a.checks
      console.log(`Audited ${a.collections} collection(s), ${a.records} record(s).`)
      const line = (label: string, rows: any[], fmt: (x: any) => string) => {
        if (!rows.length) { console.log(`  ✓ ${label}: none`); return }
        console.log(`  ⚠ ${label}: ${rows.length}`)
        rows.slice(0, 10).forEach((x) => console.log(`      ${fmt(x)}`))
        if (rows.length > 10) console.log(`      …and ${rows.length - 10} more`)
      }
      line('records with an off-lifecycle status', c.invalid_status, (x) => `${x.collection}: ${x.reason} (${x.record_id})`)
      line('records that violate the schema', c.invalid_fields, (x) => `${x.collection}: ${x.reason} (${x.record_id})`)
      line('drifted records (source changed, unreviewed)', c.drifted, (x) => `${x.collection} (${x.record_id})`)
      line('orphaned entities (no record links them)', c.orphaned_entities, (x) => `${x.kind} ${x.name} (${x.id})`)
      line('empty active collections', c.empty_collections, (x) => `${x.name} (${x.id})`)
      await flushAndExit(0)
    }

    // ── context: the Knowledge this collection reads before acting ────────────
    if (sub === 'context') {
      const cid = positional[1]
      if (!cid) { console.error('Error: `collection context <collection-id>`.'); process.exit(1) }
      const a = await api.get(`/workspaces/${ws}/collections/${cid}/context`) as any
      if (isJSON) { emitJSON(a); await flushAndExit(0) }
      if (!a.criterion.length && !a.missing.length) {
        console.log('No related knowledge — set it with `collection update <id> --criterion "slug,slug"`.'); await flushAndExit(0)
      }
      console.log(`Reads ${a.criterion.length} topic(s) before acting:`)
      a.criterion.forEach((t: any) => console.log(`  • ${t.slug}${t.status === 'reviewed' ? ' [Knowledge]' : ' [Note]'} — ${t.what ?? t.title ?? ''}`))
      if (a.missing.length) console.log(`  ⚠ unresolved (no such topic): ${a.missing.join(', ')}`)
      await flushAndExit(0)
    }

    // ── collection surface ──────────────────────────────────────────────────
    if (sub === 'add') {
      const name = positional[1]
      if (!name) { console.error('Error: `collection add <name> --archetype <…>`.'); process.exit(1) }
      const archetype = str('archetype')
      if (!archetype) { console.error('Error: --archetype is required (pipeline | analysis | content).'); process.exit(1) }
      const body: Record<string, unknown> = { name, archetype }
      if (str('owner')) body['owner_clerk_id'] = str('owner')
      const schema = readJsonFlag(flags['schema']); if (schema !== undefined) body['record_schema'] = schema
      const views = readJsonFlag(flags['views']); if (views !== undefined) body['views'] = views
      const distill = readJsonFlag(flags['distill']); if (distill !== undefined) body['distill_policy'] = distill
      if (str('criterion')) body['criterion_rel_slugs'] = str('criterion')!.split(',').map((s) => s.trim()).filter(Boolean)
      const created = await api.post(`/workspaces/${ws}/collections`, body)
      analyticsEvent('collection_add', ws, { archetype })
      if (isJSON) emitJSON(created); else console.log(`Collection '${name}' (${archetype}) created.`)
      await flushAndExit(0)
    }
    if (sub === 'list') {
      const params = new URLSearchParams({ limit: String(Math.max(1, Math.min(50, str('limit') === undefined ? 20 : Number(str('limit'))))) })
      if (str('status')) params.set('status', str('status')!)
      if (str('owner')) params.set('owner', str('owner')!)
      if (str('updated-after')) params.set('updated_after', str('updated-after')!)
      if (str('cursor')) params.set('cursor', str('cursor')!)
      const page = await api.get(`/workspaces/${ws}/collections?${params}`) as any
      const rows = Array.isArray(page) ? page : page.items ?? []
      if (isJSON) emitJSON({ items: rows, nextCursor: page.nextCursor ?? null, has_more: page.has_more ?? false })
      else {
        for (const c of rows) console.log(`${c.archetype}  ${c.name}  (${c.record_count ?? 0} records)  ${c.id}`)
        if (page.nextCursor) console.log(`\nMore collections: driftless collection list --cursor ${page.nextCursor}`)
      }
      await flushAndExit(0)
    }
    if (sub === 'search') {
      const query = (str('query') ?? positional.slice(1).join(' ')).trim()
      if (!query) { console.error('Error: collection search requires an intent query.'); process.exit(1) }
      const params = new URLSearchParams({ q: query, limit: String(Math.max(1, Math.min(50, str('limit') === undefined ? 10 : Number(str('limit'))))) })
      if (str('status')) params.set('status', str('status')!)
      if (str('owner')) params.set('owner', str('owner')!)
      if (str('updated-after')) params.set('updated_after', str('updated-after')!)
      if (str('cursor')) params.set('cursor', str('cursor')!)
      if (flags['include-archived'] === true) params.set('include_archived', 'true')
      const page = await api.get(`/workspaces/${ws}/collections/search?${params}`) as any
      if (isJSON) emitJSON(page)
      else {
        for (const c of page.items ?? []) console.log(`${c.archetype}  [${c.status}]  ${c.name}  score=${c.score}  ${c.id}`)
        if (page.nextCursor) console.log(`\nMore matches: driftless collection search ${JSON.stringify(query)} --cursor ${page.nextCursor}`)
      }
      await flushAndExit(0)
    }
    if (sub === 'get') {
      const id = positional[1]
      if (!id) { console.error('Error: `collection get <id>`.'); process.exit(1) }
      const view = str('view') === 'full' ? '?view=full' : ''
      emitJSON(await api.get(`/workspaces/${ws}/collections/${id}${view}`)); await flushAndExit(0)
    }
    if (sub === 'update') {
      const id = positional[1]
      if (!id) { console.error('Error: `collection update <id> …`.'); process.exit(1) }
      if (str('status')) { console.error("Error: collection lifecycle is explicit. Use 'collection archive' or 'collection restore'."); process.exit(1) }
      const body: Record<string, unknown> = {}
      if (str('name')) body['name'] = str('name')
      const schema = readJsonFlag(flags['schema']); if (schema !== undefined) body['record_schema'] = schema
      const views = readJsonFlag(flags['views']); if (views !== undefined) body['views'] = views
      const distill = readJsonFlag(flags['distill']); if (distill !== undefined) body['distill_policy'] = distill
      const c = await api.patch(`/workspaces/${ws}/collections/${id}`, body)
      if (isJSON) emitJSON(c); else console.log(`Collection ${id} updated.`)
      await flushAndExit(0)
    }
    if (sub === 'archive' || sub === 'restore') {
      const id = positional[1]
      if (!id) { console.error(`Error: collection ${sub} requires an id.`); process.exit(1) }
      const result = await api.post(`/workspaces/${ws}/collections/${id}/${sub}`, {}) as any
      if (isJSON) emitJSON(result)
      else console.log(`Collection ${sub === 'archive' ? 'archived' : 'restored'} — version ${result.collection?.version ?? '?'}`)
      await flushAndExit(0)
    }
    if (sub === 'purge') {
      const id = positional[1]
      if (!id) { console.error('Error: collection purge requires an id.'); process.exit(1) }
      const params = new URLSearchParams()
      if (flags['dry-run'] === true) params.set('dry_run', 'true')
      if (str('expected-version')) params.set('expected_version', str('expected-version')!)
      if (!params.size) { console.error('Error: collection purge requires --dry-run first or --expected-version <n>.'); process.exit(1) }
      const result = await api.delete(`/workspaces/${ws}/collections/${id}?${params}`) as any
      if (isJSON) emitJSON(result)
      else if (result.dry_run) console.log(`Purge impact: ${JSON.stringify(result.impact)}\nConfirm with: driftless collection purge ${id} --expected-version ${result.impact.version}`)
      else console.log(`Collection purge: ${result.purge_outcome?.status}`)
      await flushAndExit(0)
    }
    console.error(`Unknown collection subcommand: '${sub}'. Try add | list | get | update | record(s).`)
    process.exit(1)
  } catch (e: any) {
    console.error(`Collection ${sub} failed: ${formatError(e)}`)
    await flushAndExit(1)
  }
}
